import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { breakpoints } from '../common/constants';
import {
    withOptimizely,
    WithOptimizelyProps,
    selectForcedFeatures,
    getOptimizelyFeatureFlag
} from 'shared/optimizely';
import {
    DFP_AD_UNIT,
    Size,
    AD_UNIT_ROOTS
} from 'shared/components/Adverts/types';
import { getPageSuffix } from 'shared/components/Adverts/lib/pageTargeting';
import { AdvertSlotDfp } from 'shared/components/Adverts/AdvertSlotDfp';
type SlotConfig = {
    listingZeroIdxPos: number;
    adUnitCode?: DFP_AD_UNIT;
    collapseEmpty?: boolean;
    sizes: Size[];
    position?: number;
};

const DEFAULT_DESKTOP_SIZES: Size[] = [[728, 90], [728, 110], 'fluid'];

export const DESKTOP_LSRP_AD_SLOT_POSITIONS: SlotConfig[] = [
    {
        listingZeroIdxPos: 2,
        collapseEmpty: true,
        sizes: DEFAULT_DESKTOP_SIZES,
        position: 0
    },
    {
        listingZeroIdxPos: 7,
        collapseEmpty: true,
        sizes: DEFAULT_DESKTOP_SIZES,
        position: 1
    },
    {
        listingZeroIdxPos: 12,
        collapseEmpty: true,
        sizes: DEFAULT_DESKTOP_SIZES,
        position: 2
    },
    {
        listingZeroIdxPos: 17,
        collapseEmpty: true,
        sizes: DEFAULT_DESKTOP_SIZES,
        position: 3
    },
    {
        listingZeroIdxPos: 22,
        collapseEmpty: true,
        sizes: DEFAULT_DESKTOP_SIZES,
        position: 4
    }
];

const DEFAULT_MOBILE_SIZES: Size[] = [[320, 50], 'fluid'];

export const MOBILE_LSRP_AD_SLOT_POSITIONS: SlotConfig[] = [
    {
        listingZeroIdxPos: 0,
        adUnitCode: DFP_AD_UNIT.ZOOPLA_EXPERIAN_LOANSWAREHOUSE_MOBILE,
        sizes: DEFAULT_MOBILE_SIZES,
        position: 0
    },
    {
        listingZeroIdxPos: 7,
        collapseEmpty: true,
        sizes: DEFAULT_MOBILE_SIZES,
        position: 1
    },
    {
        listingZeroIdxPos: 12,
        collapseEmpty: true,
        sizes: DEFAULT_MOBILE_SIZES,
        position: 2
    },
    {
        listingZeroIdxPos: 17,
        collapseEmpty: true,
        sizes: DEFAULT_MOBILE_SIZES,
        position: 3
    },
    {
        listingZeroIdxPos: 22,
        collapseEmpty: true,
        sizes: DEFAULT_MOBILE_SIZES,
        position: 4
    }
];

const getMobileFeaturedListingAdSlot = (
    listingZeroIdxPos: number
): SlotConfig => ({
    listingZeroIdxPos,
    collapseEmpty: true,
    sizes: ['fluid', ...DEFAULT_MOBILE_SIZES],
    position: 0,
    adUnitCode: DFP_AD_UNIT.ZOOPLA_MOBILE_FEATURED_LISTING_TEST
});

const IntegratedListingAdSlot: React.FC<
    {
        useMobileConfig: boolean;
        listingIndex: number;
    } & WithOptimizelyProps
> = ({ useMobileConfig, listingIndex, optimizely }) => {
    const [mobileAdSlots, setMobileAdSlots] = useState(
        MOBILE_LSRP_AD_SLOT_POSITIONS
    );
    const [desktopAdSlots] = useState(DESKTOP_LSRP_AD_SLOT_POSITIONS);
    const featureFlagOverrides = useSelector(selectForcedFeatures);

    useEffect(() => {
        if (useMobileConfig) {
            const isMobileFeaturedListingActive = getOptimizelyFeatureFlag(
                'featured_listing_mobile_native',
                featureFlagOverrides,
                optimizely
            );

            if (isMobileFeaturedListingActive) {
                const DEFAULT_INTERSTITIAL_POSITION = 7;
                const mobileFeaturedListingZeroIdxPos =
                    optimizely?.getFeatureVariable(
                        'featured_listing_mobile_native',
                        'interstitialPosition',
                        optimizely.user.id || ''
                    ) || DEFAULT_INTERSTITIAL_POSITION;
                const featuredListingAdSlot = getMobileFeaturedListingAdSlot(
                    Number(mobileFeaturedListingZeroIdxPos)
                );

                if (
                    MOBILE_LSRP_AD_SLOT_POSITIONS.some(
                        (slot) =>
                            slot.listingZeroIdxPos ===
                            mobileFeaturedListingZeroIdxPos
                    )
                ) {
                    const indexToReplace = MOBILE_LSRP_AD_SLOT_POSITIONS.findIndex(
                        (slot) =>
                            slot.listingZeroIdxPos ===
                            mobileFeaturedListingZeroIdxPos
                    );
                    const mobileAdSlotsCopy = MOBILE_LSRP_AD_SLOT_POSITIONS;
                    mobileAdSlotsCopy[indexToReplace] = featuredListingAdSlot;
                    setMobileAdSlots(mobileAdSlotsCopy);
                } else {
                    setMobileAdSlots([
                        ...MOBILE_LSRP_AD_SLOT_POSITIONS,
                        featuredListingAdSlot
                    ]);
                }
            }
        }
    }, [useMobileConfig]);

    const adArray = useMobileConfig ? mobileAdSlots : desktopAdSlots;

    const confIndex = adArray.findIndex(
        ({ listingZeroIdxPos }) => listingZeroIdxPos === listingIndex
    );

    if (confIndex === -1) return null;

    return (
        <AdvertSlotDfp
            showOnly={[useMobileConfig ? '<' : '>', breakpoints.lsrpSidebar]}
            sizes={adArray[confIndex].sizes}
            position={adArray[confIndex].position || 0}
            adUnitCode={
                adArray[confIndex].adUnitCode || [
                    AD_UNIT_ROOTS.INTERSTITIAL,
                    getPageSuffix(),
                    '/Slot-' + ((adArray[confIndex].position || 0) + 1)
                ]
            }
            collapseEmpty={adArray[confIndex].collapseEmpty}
        />
    );
};

export const IntegratedAdSlotWithOptimizely = withOptimizely(
    IntegratedListingAdSlot
);
